import { useEffect, useRef, useState } from "react";
import Cropper from "react-easy-crop";
import { useSelector } from "react-redux";
import { fetchAuthPost } from "../functions/functions";
import api from "../routes/api";

// helper: โหลดรูปจาก URL → base64
const urlToBase64 = async (url) => {
  const result = await fetchAuthPost(api.utils.getImageUriByImageUrlInGcs, {
    imageUrl: url,
  });
  const base64WithHeader = result.data;
  return base64WithHeader;
};

export const useCropImage = (initialImage) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [image, setImage] = useState(initialImage || "");
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [imageProperties, setImageProperties] = useState(null);
  const inputFileRef = useRef();

  // 🔹 โหลด initialImage ถ้าเป็น URL
  useEffect(() => {
    const loadInitial = async () => {
      if (!initialImage) return;
      if (initialImage.startsWith("http")) {
        try {
          const base64 = await urlToBase64(initialImage);
          setImage(base64);
        } catch (err) {
          console.error("Failed to load image from URL:", err);
        }
      } else {
        // ถ้าเป็น base64 อยู่แล้ว
        setImage(initialImage);
      }
    };
    loadInitial();
  }, [initialImage]);

  const onCropComplete = (croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // ตรวจสอบประเภทไฟล์
    if (!file.type.startsWith("image/")) {
      alert("Please upload a valid image file.");
      return;
    }

    setImageProperties({
      name: file.name,
      size: file.size,
      type: file.type,
    });

    const reader = new FileReader();
    reader.onload = () => {
      setImage(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleZoomChange = (event) => {
    setZoom(parseFloat(event.target.value));
  };

  const createImage = (src) =>
    new Promise((resolve, reject) => {
      const image = new Image();
      image.crossOrigin = "anonymous";
      image.onload = () => resolve(image);
      image.onerror = (error) => reject(error);
      image.src = src;
    });

  const getCroppedImg = async (maxSizeKB = 100) => {
    if (!croppedAreaPixels || !image) return null;

    const img = await createImage(image);

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    canvas.width = croppedAreaPixels.width;
    canvas.height = croppedAreaPixels.height;

    ctx.drawImage(
      img,
      croppedAreaPixels.x,
      croppedAreaPixels.y,
      croppedAreaPixels.width,
      croppedAreaPixels.height,
      0,
      0,
      croppedAreaPixels.width,
      croppedAreaPixels.height
    );

    // 🔁 ลด quality จนกว่าจะเล็กพอ
    let quality = 0.9;
    let dataUrl = canvas.toDataURL("image/jpeg", quality);
    let fileSizeKB = Math.round((dataUrl.length * 3) / 4 / 1024); // Base64 to bytes to KB

    while (fileSizeKB > maxSizeKB && quality > 0.1) {
      quality -= 0.05;
      dataUrl = canvas.toDataURL("image/jpeg", quality);
      fileSizeKB = Math.round((dataUrl.length * 3) / 4 / 1024);
    }

    // 📉 ถ้ายังใหญ่เกินไป ให้ลด resolution ด้วย
    while (fileSizeKB > maxSizeKB) {
      canvas.width *= 0.9;
      canvas.height *= 0.9;
      ctx.drawImage(
        img,
        croppedAreaPixels.x,
        croppedAreaPixels.y,
        croppedAreaPixels.width,
        croppedAreaPixels.height,
        0,
        0,
        canvas.width,
        canvas.height
      );
      dataUrl = canvas.toDataURL("image/jpeg", quality);
      fileSizeKB = Math.round((dataUrl.length * 3) / 4 / 1024);
    }

    return dataUrl;
  };

  return {
    image,
    crop,
    zoom,
    setCrop,
    setZoom,
    onCropComplete,
    handleZoomChange,
    inputFileRef,
    handleFileChange,
    setImage,
    getCroppedImg,
    imageProperties,
  };
};

export const CropImage = ({
  setSrc = () => {},
  aspect = 4 / 3,
  existingImage = "",
}) => {
  const { msg } = useSelector((state) => state.language);

  const {
    image,
    crop,
    zoom,
    setCrop,
    setZoom,
    onCropComplete,
    handleZoomChange,
    inputFileRef,
    handleFileChange,
    setImage,
    getCroppedImg,
    imageProperties,
  } = useCropImage(existingImage);
  return (
    <div className="CropImage">
      <section>
        <div>
          <div className="preview">
            {image ? (
              <div className="crop-zone">
                <Cropper
                  image={image}
                  crop={crop}
                  zoom={zoom}
                  aspect={aspect}
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onCropComplete={onCropComplete}
                />
              </div>
            ) : (
              <div>{msg.no_image_selected}</div>
            )}
          </div>
          {image && (
            <input
              type="range"
              min={1}
              max={3}
              step={0.01}
              value={zoom}
              onChange={handleZoomChange}
            />
          )}
        </div>
        <input
          hidden
          ref={inputFileRef}
          type="file"
          accept="image/*"
          onChange={handleFileChange}
        />
        <div>
          <div className="button-group">
            <button
              className="_fill_button"
              onClick={() => {
                inputFileRef.current.value = "";
                inputFileRef.current.click();
              }}
            >
              {msg.upload_image}
            </button>
            <button
              className="_outline_button"
              onClick={() => {
                inputFileRef.current.value = "";
                setImage("");
                setSrc("");
              }}
            >
              {msg.clear}
            </button>
            <button
              hidden={!image}
              className="_outline_button"
              onClick={async () => {
                const croppedImage = await getCroppedImg(100); // บีบอัดรูปให้ขนาดไฟล์ไม่เกิน 100kB
                if (croppedImage) {
                  setSrc({ src: croppedImage, name: imageProperties?.name });
                }
              }}
            >
              {msg.submit}
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};
